import React, { useState, useEffect, useCallback } from 'react';
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  RefreshCw,
  Globe,
  BarChart3,
  PieChart,
  AlertCircle,
  ShoppingCart,
  Activity,
  Clock,
  CheckSquare,
  Square,
  MinusSquare,
  Info,
  Zap,
  Download,
  PlusCircle,
  X,
} from 'lucide-react';
import Layout from '../components/Layout';
import Loading from '../components/Loading';

const fmtUsd = (v) => {
  const n = parseFloat(v) || 0;
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD',
    minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
};
const fmtPct = (v) => {
  const n = parseFloat(v) || 0;
  return `${n >= 0 ? '+' : ''}${n.toFixed(2)}%`;
};
const colorPnl = (v) => (parseFloat(v) >= 0 ? 'text-green-600' : 'text-red-600');

const apiFetch = async (url, opts = {}) => {
  const token = localStorage.getItem('token');
  const res = await fetch(url, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(opts.headers || {}),
    },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
};

// ── 수동 등록 모달 ──────────────────────────────
const ManualAddModal = ({ onClose, onSuccess }) => {
  const [form, setForm] = useState({
    stock_code: '', stock_name: '', exchange: 'NAS',
    quantity: '', avg_buy_price: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const handleSubmit = async () => {
    if (!form.stock_code || !form.quantity || !form.avg_buy_price) {
      setError('티커, 수량, 평균매입가는 필수입니다'); return;
    }
    setLoading(true); setError('');
    try {
      const res = await apiFetch('/trading-api/us/trade/holdings/manual', {
        method: 'POST',
        body: JSON.stringify({
          stock_code:    form.stock_code.toUpperCase().trim(),
          stock_name:    form.stock_name.trim(),
          exchange:      form.exchange,
          quantity:      parseFloat(form.quantity),
          avg_buy_price: parseFloat(form.avg_buy_price),
        }),
      });
      if (res.success) {
        onSuccess(`${res.stock_code} ${res.total_quantity}주 등록 완료`);
        onClose();
      } else {
        setError(res.message || '등록 실패');
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold text-gray-900">수동 보유 종목 등록</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          KIS 앱에서 수동으로 매수한 종목을 대시보드에 등록합니다.
        </p>

        {error && (
          <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                티커 <span className="text-red-500">*</span>
              </label>
              <input
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm uppercase
                           focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="예: META"
                value={form.stock_code}
                onChange={e => setForm(f => ({ ...f, stock_code: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">거래소</label>
              <select
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm
                           focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={form.exchange}
                onChange={e => setForm(f => ({ ...f, exchange: e.target.value }))}
              >
                <option value="NAS">NASDAQ</option>
                <option value="NYS">NYSE</option>
                <option value="AMS">AMEX</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">종목명 (선택)</label>
            <input
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm
                         focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="예: Meta Platforms"
              value={form.stock_name}
              onChange={e => setForm(f => ({ ...f, stock_name: e.target.value }))}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                보유 수량 <span className="text-red-500">*</span>
              </label>
              <input
                type="number" min="0" step="1"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm
                           focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="예: 2"
                value={form.quantity}
                onChange={e => setForm(f => ({ ...f, quantity: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                평균매입가 (USD) <span className="text-red-500">*</span>
              </label>
              <input
                type="number" min="0" step="0.01"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm
                           focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="예: 612.50"
                value={form.avg_buy_price}
                onChange={e => setForm(f => ({ ...f, avg_buy_price: e.target.value }))}
              />
            </div>
          </div>
        </div>

        <div className="flex gap-3 mt-5">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700
                       hover:bg-gray-50 transition-colors"
          >
            취소
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium
                       hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {loading ? '등록 중...' : '등록'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── 메인 컴포넌트 ───────────────────────────────
const UsPortfolioPage = () => {
  const [settings, setSettings]   = useState(null);
  const [holdings, setHoldings]   = useState([]);
  const [recentTx, setRecentTx]   = useState([]);
  const [deposit, setDeposit]     = useState(null);   // USD 예수금
  const [depositLoading, setDepositLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [error, setError]         = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);

  const [selected, setSelected]   = useState(new Set());
  const [isSelling, setIsSelling] = useState(false);
  const [showManualModal, setShowManualModal] = useState(false);

  const showSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 4000);
  };

  // USD 예수금 로드
  const loadDeposit = useCallback(async () => {
    setDepositLoading(true);
    try {
      const data = await apiFetch('/trading-api/us/trade/deposit');
      if (data?.success) setDeposit(data);
    } catch (e) {
      console.warn('USD 예수금 조회 실패:', e.message);
    } finally {
      setDepositLoading(false);
    }
  }, []);

  const loadAll = useCallback(async (refresh = false) => {
    try {
      if (refresh) setIsRefreshing(true);
      else setIsLoading(true);
      setError('');

      const settingsData = await apiFetch('/api/ai-investment/settings').catch(() => null);
      if (settingsData) setSettings(settingsData);

      const holdingsData = await apiFetch('/trading-api/us/trade/holdings').catch(() => ({ holdings: [] }));
      const hList = Array.isArray(holdingsData?.holdings) ? holdingsData.holdings : [];

      const updatedHoldings = await Promise.all(
        hList.map(async (h) => {
          try {
            const p = await apiFetch(
              `/trading-api/us/trade/price/${h.stock_code}?exchange=${h.exchange || 'NAS'}`
            );
            if (p?.success && p.current_price) {
              const avg  = parseFloat(h.avg_buy_price) || 0;
              const curr = parseFloat(p.current_price);
              const qty  = parseFloat(h.total_quantity) || 0;
              return { ...h, current_price: curr, eval_amount: curr * qty,
                        pnl_amount: (curr - avg) * qty,
                        pnl_pct: avg > 0 ? ((curr - avg) / avg) * 100 : 0 };
            }
          } catch { /* 현재가 조회 실패 시 원본 유지 */ }
          const avg  = parseFloat(h.avg_buy_price)  || 0;
          const curr = parseFloat(h.current_price)  || avg;
          const qty  = parseFloat(h.total_quantity) || 0;
          return { ...h, current_price: curr, eval_amount: curr * qty,
                    pnl_amount: (curr - avg) * qty,
                    pnl_pct: avg > 0 ? ((curr - avg) / avg) * 100 : 0 };
        })
      );
      setHoldings(updatedHoldings);

      const txData = await apiFetch('/analysis-api/api/us/realized-transactions?limit=20')
        .catch(() => ({ data: [] }));
      setRecentTx(Array.isArray(txData?.data) ? txData.data : []);

      setLastUpdated(new Date());
    } catch (e) {
      setError(e.message || '데이터 로드 실패');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  useEffect(() => { loadAll(); loadDeposit(); }, [loadAll, loadDeposit]);

  // ── KIS 잔고 동기화 ────────────────────────
  const handleSyncFromKis = async () => {
    setIsSyncing(true); setError('');
    try {
      const res = await apiFetch('/trading-api/us/trade/sync-from-kis', { method: 'POST' });
      if (res.success) {
        showSuccess(`✅ KIS 동기화 완료: ${res.synced}개 종목 반영`);
        await loadAll(true);
      } else {
        setError(res.message || 'KIS 동기화 실패');
      }
    } catch (e) {
      setError(`KIS 동기화 실패: ${e.message}`);
    } finally {
      setIsSyncing(false);
    }
  };

  // ── 선택 로직 ──────────────────────────────
  const toggleAll = () => {
    if (selected.size === holdings.length) setSelected(new Set());
    else setSelected(new Set(holdings.map((h) => h.stock_code)));
  };
  const toggleOne = (code) => {
    const s = new Set(selected);
    s.has(code) ? s.delete(code) : s.add(code);
    setSelected(s);
  };
  const isAllSel  = holdings.length > 0 && selected.size === holdings.length;
  const isPartSel = selected.size > 0 && selected.size < holdings.length;

  // ── 선택 매도 ──────────────────────────────
  const handleSellSelected = async () => {
    if (selected.size === 0) { alert('매도할 종목을 선택해주세요.'); return; }
    const names = holdings.filter((h) => selected.has(h.stock_code))
      .map((h) => h.stock_code).join(', ');
    if (!window.confirm(`⚠️ ${selected.size}개 종목 매도: ${names}\n\n취소 불가합니다.`)) return;

    setIsSelling(true);
    let ok = 0; let fail = 0;
    try {
      for (const h of holdings.filter((h) => selected.has(h.stock_code))) {
        try {
          const res = await apiFetch('/trading-api/us/trade/order', {
            method: 'POST',
            body: JSON.stringify({
              stock_code: h.stock_code, exchange: h.exchange || 'NAS',
              order_type: 'SELL', quantity: Math.floor(parseFloat(h.total_quantity)),
              order_reason: '대시보드 선택 매도', initiator_type: 'MANUAL',
            }),
          });
          res?.success ? ok++ : fail++;
        } catch { fail++; }
      }
      alert(`✅ 매도 완료: 성공 ${ok}건 / 실패 ${fail}건`);
      setSelected(new Set());
      loadAll(true);
    } finally {
      setIsSelling(false);
    }
  };

  const totalEval   = holdings.reduce((s, h) => s + (parseFloat(h.eval_amount)   || 0), 0);
  const totalCost   = holdings.reduce((s, h) => s + (parseFloat(h.avg_buy_price) || 0) * (parseFloat(h.total_quantity) || 0), 0);
  const totalPnl    = totalEval - totalCost;
  const totalPnlPct = totalCost > 0 ? (totalPnl / totalCost) * 100 : 0;
  const usLimit     = parseFloat(settings?.us_investment_limit || 0);
  const autoEnabled = settings?.us_auto_trading_enabled;

  if (isLoading) {
    return (
      <Layout>
        <div className="flex justify-center py-12">
          <Loading size="large" message="미국 주식 포트폴리오 로딩 중..." />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 py-8 space-y-6">

        {/* ── 수동 등록 모달 ── */}
        {showManualModal && (
          <ManualAddModal
            onClose={() => setShowManualModal(false)}
            onSuccess={(msg) => { showSuccess(msg); loadAll(true); }}
          />
        )}

        {/* ── 헤더 ── */}
        <div className="flex justify-between items-start flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
              <Globe className="w-8 h-8 text-blue-500" />
              미국 주식 포트폴리오
            </h1>
            <p className="text-gray-500 mt-1">KIS API를 통한 미국 주식 자동매매 현황</p>
            {lastUpdated && (
              <p className="text-xs text-gray-400 mt-1">
                마지막 업데이트: {lastUpdated.toLocaleTimeString()}
              </p>
            )}
          </div>
          <div className="flex gap-2 flex-wrap">
            {/* KIS 잔고 동기화 버튼 */}
            <button
              onClick={handleSyncFromKis}
              disabled={isSyncing}
              title="KIS 실제 잔고를 가져와서 대시보드에 반영합니다"
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg
                         hover:bg-green-700 disabled:opacity-50 transition-all text-sm"
            >
              <Download className={`h-4 w-4 mr-2 ${isSyncing ? 'animate-bounce' : ''}`} />
              {isSyncing ? 'KIS 동기화 중...' : 'KIS 잔고 동기화'}
            </button>
            {/* 수동 등록 버튼 */}
            <button
              onClick={() => setShowManualModal(true)}
              title="수동 매수한 종목을 직접 입력해서 등록합니다"
              className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg
                         hover:bg-indigo-700 transition-all text-sm"
            >
              <PlusCircle className="h-4 w-4 mr-2" />
              수동 등록
            </button>
            {/* 새로고침 버튼 */}
            <button
              onClick={() => loadAll(true)}
              disabled={isRefreshing}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg
                         hover:bg-blue-700 disabled:opacity-50 transition-all text-sm"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
              {isRefreshing ? '업데이트 중...' : '새로고침'}
            </button>
          </div>
        </div>

        {/* ── 성공 메시지 ── */}
        {successMsg && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center">
            <Activity className="h-5 w-5 text-green-600 mr-2 flex-shrink-0" />
            <p className="text-green-800 text-sm font-medium">{successMsg}</p>
          </div>
        )}

        {/* ── 에러 ── */}
        {error && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-center">
            <AlertCircle className="h-5 w-5 text-yellow-600 mr-2 flex-shrink-0" />
            <p className="text-yellow-800 text-sm">{error}</p>
          </div>
        )}

        {/* ── 설정 상태 배너 ── */}
        {settings && (
          <div className={`rounded-lg p-4 flex items-center justify-between
            ${autoEnabled ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200'}`}>
            <div className="flex items-center gap-2">
              <Zap className={`h-5 w-5 ${autoEnabled ? 'text-green-600' : 'text-gray-400'}`} />
              <div>
                <span className={`font-semibold ${autoEnabled ? 'text-green-700' : 'text-gray-600'}`}>
                  미국 주식 자동매매 {autoEnabled ? '활성' : '비활성'}
                </span>
                <span className="text-sm text-gray-500 ml-3">
                  투자 한도: {usLimit > 0 ? fmtUsd(usLimit) : '미설정'}
                </span>
              </div>
            </div>
            {!autoEnabled && (
              <a href="/settings" className="text-sm text-blue-600 hover:underline">설정하기 →</a>
            )}
          </div>
        )}

        {/* ── USD 예수금 카드 ── */}
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center">
                <DollarSign className="w-4 h-4 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 text-sm">USD 예수금</h3>
                <p className="text-xs text-gray-500">KIS 해외주식 예수금 현황</p>
              </div>
            </div>
            <button
              onClick={loadDeposit}
              disabled={depositLoading}
              className="flex items-center gap-1 text-xs text-green-600 hover:text-green-800 transition-colors"
            >
              <RefreshCw className={`w-3 h-3 ${depositLoading ? 'animate-spin' : ''}`} />
              {depositLoading ? '조회 중...' : '새로고침'}
            </button>
          </div>

          {deposit ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-white rounded-xl p-3 border border-green-100">
                <p className="text-xs text-gray-500 mb-1">주문가능금액</p>
                <p className="text-lg font-bold text-green-600">{fmtUsd(deposit.available_order_usd)}</p>
                <p className="text-xs text-gray-400 mt-0.5">즉시 투자 가능</p>
              </div>
              <div className="bg-white rounded-xl p-3 border border-green-100">
                <p className="text-xs text-gray-500 mb-1">외화예수금</p>
                <p className="text-lg font-bold text-gray-800">{fmtUsd(deposit.total_deposit_usd)}</p>
                <p className="text-xs text-gray-400 mt-0.5">결제 전 잔액 (D+2)</p>
              </div>
              <div className="bg-white rounded-xl p-3 border border-green-100">
                <p className="text-xs text-gray-500 mb-1">외화인출가능</p>
                <p className="text-lg font-bold text-gray-800">{fmtUsd(deposit.withdraw_possible_usd)}</p>
                <p className="text-xs text-gray-400 mt-0.5">즉시 인출 가능액</p>
              </div>
              <div className="bg-white rounded-xl p-3 border border-green-100">
                <p className="text-xs text-gray-500 mb-1">적용환율</p>
                <p className="text-lg font-bold text-gray-800">
                  {deposit.exchange_rate
                    ? `\u20a9${deposit.exchange_rate.toLocaleString()}`
                    : '-'}
                </p>
                <p className="text-xs text-gray-400 mt-0.5">KIS 기준 USD/KRW</p>
              </div>
            </div>
          ) : (
            <div className="text-center py-4">
              {depositLoading ? (
                <div className="flex items-center justify-center gap-2 text-gray-400 text-sm">
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  <span>예수금 조회 중...</span>
                </div>
              ) : (
                <p className="text-sm text-gray-400">KIS API 키 또는 토큰을 확인해주세요</p>
              )}
            </div>
          )}
          {deposit && (
            <p className="text-xs text-gray-400 text-right mt-2">
              조회시각: {new Date(deposit.query_time).toLocaleTimeString()}
            </p>
          )}
        </div>

        {/* ── 요약 카드 ── */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl shadow-lg text-white p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm">총 평가금액</p>
                <p className="text-2xl font-bold mt-1">{fmtUsd(totalEval)}</p>
                <p className="text-blue-100 text-xs mt-1">{holdings.length}종목 보유</p>
              </div>
              <div className="h-12 w-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                <DollarSign className="h-6 w-6 text-white" />
              </div>
            </div>
          </div>

          <div className={`rounded-xl shadow-lg text-white p-6 bg-gradient-to-r
            ${totalPnl >= 0 ? 'from-green-500 to-green-600' : 'from-red-500 to-red-600'}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white text-sm opacity-80">평가 손익</p>
                <p className="text-2xl font-bold mt-1">{fmtUsd(totalPnl)}</p>
                <p className="text-white text-xs mt-1 opacity-80">{fmtPct(totalPnlPct)}</p>
              </div>
              <div className="h-12 w-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                {totalPnl >= 0 ? <TrendingUp className="h-6 w-6 text-white" />
                               : <TrendingDown className="h-6 w-6 text-white" />}
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl shadow-lg text-white p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm">한도 사용률</p>
                <p className="text-2xl font-bold mt-1">
                  {usLimit > 0 ? `${((totalCost / usLimit) * 100).toFixed(1)}%` : '-'}
                </p>
                <p className="text-purple-100 text-xs mt-1">
                  한도 {usLimit > 0 ? fmtUsd(usLimit) : '미설정'}
                </p>
              </div>
              <div className="h-12 w-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                <PieChart className="h-6 w-6 text-white" />
              </div>
            </div>
          </div>
        </div>

        {/* ── 보유 종목 테이블 ── */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-5 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-semibold text-gray-900">보유 종목</h2>
              <span className="text-sm text-gray-500">({holdings.length}개)</span>
              {selected.size > 0 && (
                <span className="text-sm text-blue-600 bg-blue-50 px-2 py-0.5 rounded">
                  {selected.size}개 선택
                </span>
              )}
            </div>
            {holdings.length > 0 && (
              <button
                onClick={handleSellSelected}
                disabled={isSelling || selected.size === 0}
                className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-all
                  ${selected.size > 0
                    ? 'bg-red-600 text-white hover:bg-red-700'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  } disabled:opacity-50`}
              >
                <ShoppingCart className="h-4 w-4 mr-2" />
                {isSelling ? '매도 중...' : `선택 매도 (${selected.size})`}
              </button>
            )}
          </div>

          {holdings.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              <Globe className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p className="text-lg font-medium">보유 중인 미국 주식이 없습니다.</p>
              <p className="text-sm mt-1 mb-4">
                {autoEnabled
                  ? '자동매매가 활성 상태입니다. 오늘 밤 장 시작 시 매수 예정입니다.'
                  : '설정 페이지에서 미국 주식 자동매매를 활성화하세요.'}
              </p>
              <div className="flex justify-center gap-3">
                <button
                  onClick={handleSyncFromKis}
                  disabled={isSyncing}
                  className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg
                             text-sm hover:bg-green-700 disabled:opacity-50"
                >
                  <Download className="h-4 w-4 mr-2" />
                  {isSyncing ? '동기화 중...' : 'KIS 잔고 동기화'}
                </button>
                <button
                  onClick={() => setShowManualModal(true)}
                  className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg
                             text-sm hover:bg-indigo-700"
                >
                  <PlusCircle className="h-4 w-4 mr-2" />
                  수동 등록
                </button>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3">
                      <button onClick={toggleAll} className="flex items-center justify-center text-gray-400 hover:text-blue-600">
                        {isAllSel ? <CheckSquare className="h-5 w-5 text-blue-600" />
                          : isPartSel ? <MinusSquare className="h-5 w-5 text-blue-600" />
                          : <Square className="h-5 w-5" />}
                      </button>
                    </th>
                    {['티커', '거래소', '보유수량', '평균매입가', '현재가', '평가금액', '손익', '수익률'].map((h) => (
                      <th key={h} className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-right first:text-left">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {holdings.map((h, i) => {
                    const isSel = selected.has(h.stock_code);
                    const pnl   = parseFloat(h.pnl_amount) || 0;
                    const pct   = parseFloat(h.pnl_pct)    || 0;
                    return (
                      <tr key={`${h.stock_code}-${i}`}
                          className={`hover:bg-gray-50 cursor-pointer ${isSel ? 'bg-blue-50' : ''}`}
                          onClick={() => toggleOne(h.stock_code)}>
                        <td className="px-4 py-4" onClick={e => e.stopPropagation()}>
                          <button onClick={() => toggleOne(h.stock_code)} className="flex items-center justify-center">
                            {isSel ? <CheckSquare className="h-5 w-5 text-blue-600" />
                                   : <Square className="h-5 w-5 text-gray-400 hover:text-blue-600" />}
                          </button>
                        </td>
                        <td className="px-4 py-4 text-left">
                          <p className="font-semibold text-gray-900">{h.stock_code}</p>
                          <p className="text-xs text-gray-400">{h.stock_name || ''}</p>
                        </td>
                        <td className="px-4 py-4 text-right text-sm text-gray-700">{h.exchange || 'NAS'}</td>
                        <td className="px-4 py-4 text-right text-sm text-gray-900">{parseFloat(h.total_quantity).toFixed(4)}주</td>
                        <td className="px-4 py-4 text-right text-sm text-gray-900">{fmtUsd(h.avg_buy_price)}</td>
                        <td className="px-4 py-4 text-right text-sm text-gray-900">{fmtUsd(h.current_price)}</td>
                        <td className="px-4 py-4 text-right text-sm font-medium text-gray-900">{fmtUsd(h.eval_amount)}</td>
                        <td className={`px-4 py-4 text-right text-sm font-medium ${colorPnl(pnl)}`}>{fmtUsd(pnl)}</td>
                        <td className={`px-4 py-4 text-right text-sm font-medium ${colorPnl(pct)}`}>
                          <div className="flex items-center justify-end gap-1">
                            {pct > 0 ? <TrendingUp className="h-4 w-4" />
                              : pct < 0 ? <TrendingDown className="h-4 w-4" /> : null}
                            {fmtPct(pct)}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* ── 최근 실현 손익 ── */}
        {recentTx.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="p-5 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-blue-500" />
                최근 실현 손익
              </h2>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {['티커', '매도일', '수량', '평균매입가', '매도가', '실현손익', '수익률'].map((h) => (
                      <th key={h} className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-right first:text-left">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {recentTx.map((tx, i) => {
                    const pnl = parseFloat(tx.realized_profit) || 0;
                    const pct = parseFloat(tx.profit_rate)     || 0;
                    return (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-left font-semibold text-gray-900">{tx.stock_code}</td>
                        <td className="px-4 py-3 text-right text-sm text-gray-600">
                          {tx.sell_date ? new Date(tx.sell_date).toLocaleDateString('ko-KR') : '-'}
                        </td>
                        <td className="px-4 py-3 text-right text-sm text-gray-700">{parseFloat(tx.sell_quantity).toFixed(2)}주</td>
                        <td className="px-4 py-3 text-right text-sm text-gray-700">{fmtUsd(tx.avg_buy_price)}</td>
                        <td className="px-4 py-3 text-right text-sm text-gray-700">{fmtUsd(tx.sell_price)}</td>
                        <td className={`px-4 py-3 text-right text-sm font-medium ${colorPnl(pnl)}`}>{fmtUsd(pnl)}</td>
                        <td className={`px-4 py-3 text-right text-sm font-medium ${colorPnl(pct)}`}>{fmtPct(pct)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── 매매 일정 안내 ── */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-800">
              <p className="font-semibold mb-2">미국 주식 자동매매 스케줄 (KST 기준)</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-blue-500" />
                  <span><strong>표준시(11~3월)</strong>: 23:00 Box 분석 → 23:15 계획 → 23:30 매수</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-blue-500" />
                  <span><strong>서머타임(3~11월)</strong>: 22:00 Box 분석 → 22:15 계획 → 22:30 매수</span>
                </div>
                <div className="flex items-center gap-2">
                  <Activity className="h-4 w-4 text-blue-500" />
                  <span><strong>리스크 모니터링</strong>: 장 중 익절/손절 자동 실행</span>
                </div>
                <div className="flex items-center gap-2">
                  <ShoppingCart className="h-4 w-4 text-blue-500" />
                  <span><strong>금요일</strong>: 장마감 10분 전 전량 매도</span>
                </div>
              </div>
              <p className="mt-3 text-blue-700">
                • 익절 기준: <strong>+10%</strong> &nbsp;|&nbsp; 손절 기준: <strong>-5%</strong>
                &nbsp;|&nbsp; 투자 전략: Box 권 분석 (Mean Reversion + Breakout)
              </p>
            </div>
          </div>
        </div>

      </div>
    </Layout>
  );
};

export default UsPortfolioPage;
